import { Link, Stack } from '@chakra-ui/react'
import LazySection from 'components/common/LazySection'
import StartSurveyFixedFooter from 'components/modules/landings/prep/common/StartSurveyFixedFooter'
import AccordionNewSection from 'components/sections/AccordionNewSection'
import BulletPointsSection from 'components/sections/BulletPointsSection'
import ConsultationMethodsSection from 'components/sections/ConsultationMethodsSection'
import CoursesSection from 'components/sections/CoursesSection'
import ImageLinkSliderSection from 'components/sections/ImageLinkSliderSection'
import MensHealthSection from 'components/sections/MensHealthSection'
import OnlineSolutionsSection from 'components/sections/OnlineSolutionsSection'
import ReviewsBriefSection from 'components/sections/ReviewsBriefSection'
import ScrollingBannerSection from 'components/sections/ScrollingBannerSection'
import SmartfitSection from 'components/sections/SmartFitSection'
import SpecialistsSection from 'components/sections/SpecialistsSection'
import StepsToTreatment, { Stats } from 'components/sections/StepsToTreatment'
import { Body1, H2 } from 'components/typography'
import { useIsSmartfitClient } from 'hooks/helpers/useIsSmartfitClient'
import { LandingBasicLayout } from 'layouts/LadingLayout'
import { HomeGeo } from 'modules/landings/geo/content/home'
import GeoHead from 'modules/landings/geo/GeoHead'
import { geoFaqSections } from 'modules/landings/geo/GeoSections'
import { buildHomeJsonLd } from 'modules/landings/geo/jsonLd'
import { GeoAboutBlock, ReclameAquiStrip } from 'modules/landings/geo/PreviewSections'
import { homeImgLinkSliderData, homeImgLinkSliderDataSmartfit } from 'modules/landings/IndexPage/homeImgLinkSliderData'
import { toPlainText } from 'modules/landings/ProgramLanding/richText'
import NextLink from 'next/link'
import { FC } from 'react'

interface IndexPageProps {
  /** GEO home (PRD section 7): head and JSON-LD and the new blocks, in the current design. */
  geo: HomeGeo
}

/** The Omens home: the GEO home Morgan validated as /home-v2 (live since 28/10/2026). */
const IndexPage: FC<IndexPageProps> = ({ geo }) => {
  const isSmartfit = useIsSmartfitClient()
  return (
    <LandingBasicLayout>
      <GeoHead
        meta={geo.meta}
        canonicalPath="/"
        jsonLd={buildHomeJsonLd(geo.faq, toPlainText(geo.summary))}
        noindex={false}
      />
      <MensHealthSection conditionLinks={geo.conditionLinks} />
      {/* Morgan, 24/10/2026: the hero as today, the summary in its own section right under it */}
      <GeoAboutBlock title="O que é a Omens" text={geo.summary} />

      <LazySection>
        <ScrollingBannerSection />
      </LazySection>

      {isSmartfit && (
        <LazySection>
          <SmartfitSection />
        </LazySection>
      )}

      <LazySection>
        <ImageLinkSliderSection data={isSmartfit ? homeImgLinkSliderDataSmartfit : homeImgLinkSliderData} />
      </LazySection>

      <LazySection>
        <ReviewsBriefSection footer={<ReclameAquiStrip />} />
      </LazySection>

      <LazySection>
        <BulletPointsSection titleAs="h2" />
      </LazySection>

      <LazySection>
        <OnlineSolutionsSection />
      </LazySection>

      <LazySection>
        <ConsultationMethodsSection />
      </LazySection>

      <LazySection>
        <SpecialistsSection withCredentials />
      </LazySection>

      <LazySection>
        <StepsToTreatment showStats statsSource={geo.statsSource} afterStats={<DatafolhaBlock {...geo.datafolha} />} />
      </LazySection>

      <LazySection>
        <CoursesSection />
      </LazySection>

      <LazySection>
        <AccordionNewSection
          sections={geo.faq.map(({ question, body, answer }) =>
            body ? { title: question, body } : geoFaqSections([{ question, answer }])[0]
          )}
        />
      </LazySection>

      <StartSurveyFixedFooter type="sexual-health" />
    </LandingBasicLayout>
  )
}

export default IndexPage

/** "Pesquisas Omens + Datafolha" (PRD 7.3), in the style of the numbers block above it. */
const DatafolhaBlock: FC<HomeGeo['datafolha']> = ({ title, stats, source, link }) => (
  <Stack align="center" spacing={6} w="full" pb={{ base: '40px', lg: '60px' }}>
    <H2 color="magicmint" textAlign="center">
      {title}
    </H2>
    <Stats items={stats} source={source} pt={{ base: '24px', lg: '40px' }} pb={0} />
    <Body1 color="magicmint">
      <Link as={NextLink} href={link.href} prefetch={false} textDecoration="underline">
        {link.label}
      </Link>
    </Body1>
  </Stack>
)
